import { IGetSubscriptionSchema } from '@monetize/types/app';
import { ApiListResponse, DEFAULT_PAGER } from '@monetize/types/common';
import { pluralize } from '@monetize/utils/core';
import { ApiDataMapKey, useGetById, useGetListData } from '../api/queryUtils';
import { ID_PREFIX_TO_CONFIG, IdPrefixValue } from '../constants/common';
import { ACCOUNTS_ROLES } from '../constants/roles';
import { useAuth } from '../services/auth0';
import { isInvoiceNumberQuery } from '../utils/recordIds';

export const getConfigFromInput = (
  input: string,
  defaultApiKey: ApiDataMapKey,
): IdPrefixValue => {
  const defaultReturnValue: IdPrefixValue = {
    apiKey: defaultApiKey,
    entityName: 'Account',
    nameField: 'accountName',
    roles: ACCOUNTS_ROLES,
  };
  if (!input) {
    return defaultReturnValue;
  }
  const [prefix] = input.split('_');
  return Object.hasOwn(ID_PREFIX_TO_CONFIG, prefix)
    ? ID_PREFIX_TO_CONFIG[prefix]
    : defaultReturnValue;
};

/**
 * Invoice numbers are matched exactly, so a single page with no sort is enough.
 * (DEFAULT_PAGER would add a `sort=createDate:desc` the query does not need.)
 */
const INVOICE_SEARCH_CONFIG = { rows: DEFAULT_PAGER.rows };

interface UseGetOmniSearchListProps {
  debouncedValue: string;
  apiKey: ApiDataMapKey;
  sortField: string;
  isOpen: boolean;
}

export const useGetOmniSearchList = ({
  debouncedValue,
  apiKey,
  sortField,
  isOpen,
}: UseGetOmniSearchListProps) => {
  const { currentTenantUserHasRole } = useAuth();

  // OmniSearchInput debounces the raw input, so normalize once here and use
  // this for every lookup below.
  const searchValue = debouncedValue.trim();

  // Determine search configuration based on input prefix
  const prefixConfig = getConfigFromInput(searchValue, apiKey);
  const dynamicApiKey = prefixConfig.apiKey;

  const isSingleEntitySearch =
    dynamicApiKey !== ID_PREFIX_TO_CONFIG['acct'].apiKey;

  // Invoice numbers carry no ID prefix, so they fall through to the account
  // search above. Rather than choosing between the two, run the invoice lookup
  // alongside it and prefer the invoice only when it actually matches.
  const isInvoiceNumberSearch = isInvoiceNumberQuery(searchValue);

  // Hook calls for different search types
  // 1. General search for accounts (default fallback)
  const { data: generalData, isFetching: isGeneralFetching } = useGetListData(
    'accountSearch',
    {
      config: { ...DEFAULT_PAGER, sortField, sortOrder: 1 },
      filters: { query: searchValue },
    },
    {
      enabled: isOpen && !!searchValue && !isSingleEntitySearch,
      refetchOnWindowFocus: false,
    },
  );

  // 2. Single entity fetch by ID
  // HACK: Only call when searching for a specific entity and ignore account search
  const { data: singleEntityData, isFetching: isSingleEntityFetching } =
    useGetById(
      isSingleEntitySearch ? dynamicApiKey : 'accounts', // Fallback to accounts to avoid errors
      searchValue,
      {
        enabled: !!searchValue && isSingleEntitySearch,
        refetchOnWindowFocus: false,
        meta: { showErrorToast: false },
        select: (data) => {
          let responsePayload = data;
          if (dynamicApiKey === ID_PREFIX_TO_CONFIG['subs'].apiKey) {
            responsePayload = {
              ...(responsePayload as IGetSubscriptionSchema),
              accountName:
                (responsePayload as IGetSubscriptionSchema)?.account?.name ||
                '',
            };
          }

          return {
            totalElements: 1,
            totalPages: 1,
            content: data ? [responsePayload] : [],
          } as ApiListResponse;
        },
      },
    );

  // 3. Invoice number lookup, e.g. `100000034-1`
  const { data: invoiceData, isFetching: isInvoiceFetching } = useGetListData(
    'invoices',
    {
      config: INVOICE_SEARCH_CONFIG,
      filters: { invoiceNumber: searchValue },
    },
    {
      enabled: isOpen && isInvoiceNumberSearch,
      refetchOnWindowFocus: false,
    },
  );

  // Use appropriate data based on search type
  const hasInvoiceMatch =
    isInvoiceNumberSearch && !!invoiceData?.content.length;

  let data: ApiListResponse | undefined;
  let isFetching: boolean;
  // Display config follows whichever source answered the query
  let activeConfig = prefixConfig;

  if (hasInvoiceMatch) {
    data = invoiceData;
    isFetching = isInvoiceFetching;
    activeConfig = ID_PREFIX_TO_CONFIG['invc'];
  } else if (isSingleEntitySearch) {
    data = singleEntityData;
    isFetching = isSingleEntityFetching;
  } else {
    data = generalData;
    // Hold the spinner until the invoice lookup settles too, so a fast empty
    // account response does not flash "No results" over a pending invoice hit.
    isFetching = isInvoiceNumberSearch
      ? isGeneralFetching || isInvoiceFetching
      : isGeneralFetching;
  }

  if (activeConfig?.roles && !currentTenantUserHasRole?.(activeConfig.roles)) {
    data = {
      pageable: undefined,
      totalElements: 0,
      totalPages: 0,
      content: [],
    };
  }

  return {
    data,
    isFetching,
    fieldConfig: {
      nameField: activeConfig.nameField,
      entityDisplayName: pluralize(
        activeConfig.entityName,
        2,
        's',
        activeConfig.plural,
      ),
    },
  };
};
